import React, { useRef, useCallback, useEffect, useState } from 'react';
import Webcam from 'react-webcam';

// Import utilities
import { loadOpenCV, detectAlignmentWithOpenCV } from './utils/opencvUtils';
import { detectAlignmentFallback } from './utils/alignmentUtils';
import {
  getWebcamProps,
  calculateCameraDimensions,
  cropImageToGuidingFrame,
  WebcamPropsType,
  calculateGuidingFrameForCanvas,
} from './utils/cameraUtils';
import {
  startCountdown,
  createSafeAlignmentChecker,
} from './utils/countdownUtils';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { ICONS } from '@/constants/icons';

interface CheckCameraCaptureProps {
  onCapture: (imageSrc: string) => void;
  onClose: () => void;
  title?: string;
  aspectRatio?: number;
}

const isMobile =
  /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

const CheckCameraCapture: React.FC<CheckCameraCaptureProps> = ({
  onCapture,
  onClose,
  title = 'Position check within frame',
  aspectRatio = 2,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDetectingRef = useRef(true);

  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isOpenCVReady, setIsOpenCVReady] = useState(false);
  const [isAligned, setIsAligned] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [webcamProps, setWebcamProps] = useState<WebcamPropsType | null>(null);

  const alignmentCleanupRef = useRef<(() => void) | null>(null);
  const countdownCleanupRef = useRef<(() => void) | null>(null);

  // Calculate dimensions based on aspect ratio
  const getDimensions = () => {
    return calculateCameraDimensions(aspectRatio, isMobile);
  };

  // Initialize camera and OpenCV
  useEffect(() => {
    const initializeCamera = async () => {
      try {
        console.log('🔧 Initializing camera with adaptive quality...');
        // Use high-quality settings for better image capture
        const webcamProps = await getWebcamProps(aspectRatio);
        setWebcamProps(webcamProps);

        // Extract quality info from video constraints
        const constraints = webcamProps.videoConstraints;
        if (constraints && 'width' in constraints && 'height' in constraints) {
          const widthConstraint = constraints.width;
          const heightConstraint = constraints.height;

          const width =
            widthConstraint &&
            typeof widthConstraint === 'object' &&
            'ideal' in widthConstraint
              ? String(widthConstraint.ideal) || 'Unknown'
              : 'Unknown';
          const height =
            heightConstraint &&
            typeof heightConstraint === 'object' &&
            'ideal' in heightConstraint
              ? String(heightConstraint.ideal) || 'Unknown'
              : 'Unknown';

          console.log(`✅ Camera initialized with quality: ${width}x${height}`);

          if (isMobile) {
            console.log(
              '📱 Mobile: Using native camera input for maximum quality',
            );
          } else {
            console.log('🖥️ Desktop: Using enhanced webcam capture');
          }
        }
      } catch (error) {
        console.error('Failed to initialize camera:', error);
        setCameraError(
          error instanceof Error
            ? error.message
            : 'Failed to initialize camera. Please check camera permissions.',
        );
      }
    };

    const initializeOpenCV = async () => {
      try {
        await loadOpenCV();
        setIsOpenCVReady(true);
      } catch (error) {
        console.error('Failed to initialize OpenCV:', error);
        setCameraError(
          error instanceof Error
            ? error.message
            : 'Failed to load OpenCV. Please check your internet connection.',
        );
      }
    };

    void initializeCamera();
    void initializeOpenCV();

    return () => {
      if (alignmentCleanupRef.current) {
        alignmentCleanupRef.current();
      }
    };
  }, [aspectRatio, isMobile]);

  // Alignment detection functions
  const checkOpenCVAlignment = useCallback(() => {
    if (!webcamRef.current || !canvasRef.current) return false;
    const video = webcamRef.current.video;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!video || !ctx || video.videoWidth === 0 || video.videoHeight === 0) {
      return false;
    }

    const guidingFrame = calculateGuidingFrameForCanvas(
      video.videoWidth,
      video.videoHeight,
    );

    try {
      const result = detectAlignmentWithOpenCV(
        canvas,
        ctx,
        video,
        guidingFrame,
      );
      return result.isAligned;
    } catch (error) {
      console.error('[checkOpenCVAlignment] error:', error);
      return false;
    }
  }, [aspectRatio, isMobile]);

  const checkFallbackAlignment = useCallback(() => {
    if (!webcamRef.current || !canvasRef.current) return false;
    const video = webcamRef.current.video;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!video || !ctx || video.videoWidth === 0 || video.videoHeight === 0) {
      return false;
    }
    const result = detectAlignmentFallback(canvas, ctx, video);
    return result.isAligned;
  }, []);

  useEffect(() => {
    if (capturedImage) return;

    const alignmentChecker = isOpenCVReady
      ? checkOpenCVAlignment
      : checkFallbackAlignment;
    const interval = isOpenCVReady ? 500 : 1000;

    alignmentCleanupRef.current = createSafeAlignmentChecker(
      () => {
        if (isDetectingRef.current) {
          return alignmentChecker();
        }
        return false;
      },
      (aligned) => {
        if (isDetectingRef.current && aligned) {
          setIsAligned(true);
          isDetectingRef.current = false; // Lock detection until after capture
        }
      },
      interval,
    );

    return () => {
      if (alignmentCleanupRef.current) {
        alignmentCleanupRef.current();
        alignmentCleanupRef.current = null;
      }
    };
  }, [
    isOpenCVReady,
    capturedImage,
    checkOpenCVAlignment,
    checkFallbackAlignment,
  ]);

  // Auto-capture when aligned
  useEffect(() => {
    if (isAligned && !capturedImage && countdown === null) {
      setCountdown(3);
      countdownCleanupRef.current = startCountdown(
        3,
        (count) => setCountdown(count),
        async () => {
          setCountdown(null);
          await handleCapture();
        },
      );
    }

    return () => {
      if (!isAligned || capturedImage) {
        if (countdownCleanupRef.current) {
          countdownCleanupRef.current();
          countdownCleanupRef.current = null;
        }
        setCountdown(null);
      }
    };
  }, [isAligned, capturedImage]);

  // Event handlers
  const handleCapture = useCallback(async () => {
    console.log('🔧 Handle Capture - Starting capture process...');

    if (!webcamRef.current) {
      console.error('❌ Webcam ref is not available');
      setCameraError('Camera not available. Please refresh and try again.');
      return;
    }

    console.log('🔧 Getting screenshot from webcam...');
    const imageSrc = webcamRef.current.getScreenshot();

    if (!imageSrc) {
      console.error('❌ Failed to get screenshot from webcam');
      setCameraError(
        'Failed to capture image. Please check camera permissions.',
      );
      return;
    }

    console.log(
      '🔧 Screenshot captured successfully, starting crop process...',
    );

    // check size of imageSrc
    const image = new Image();
    image.src = imageSrc;
    console.log('🔧 Image size:', webcamRef.current);
    console.log('🔧 Image imageSrc:', imageSrc);

    try {
      const croppedImageSrc = await cropImageToGuidingFrame(
        imageSrc,
        aspectRatio,
        isMobile,
      );
      setCapturedImage(croppedImageSrc);
      setIsAligned(false);
      isDetectingRef.current = true;
    } catch (error) {
      console.error(
        '❌ Error cropping image:',
        error instanceof Error ? error.message : String(error),
      );
      console.log('🔧 Using original image as fallback');
      // Fallback to original image
      setCapturedImage(imageSrc);
      setIsAligned(false);
      isDetectingRef.current = true;
    }
  }, [aspectRatio]);

  const handleUseImage = useCallback(() => {
    if (capturedImage) {
      onCapture(capturedImage);
    }
  }, [capturedImage, onCapture]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    setIsAligned(false);
    setCountdown(null);
    isDetectingRef.current = true;
  }, []);

  const renderErrorView = (
    cameraError: string,
    onClose: () => void,
  ): JSX.Element => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant="h6"
        sx={{ color: 'white', textAlign: 'center', mb: 2 }}
      >
        {cameraError}
      </Typography>
      <Button
        variant="contained"
        onClick={onClose}
        sx={{ backgroundColor: '#254D73', color: 'white' }}
      >
        Close
      </Button>
    </Box>
  );

  // Render error state
  if (cameraError) {
    return renderErrorView(cameraError, onClose);
  }

  const renderHeader = (title: string, onClose: () => void): JSX.Element => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 2rem',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1,
      }}
    >
      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
        {title}
      </Typography>
      <IconButton onClick={onClose} sx={{ color: 'white' }}>
        <ICONS.Close size={24} />
      </IconButton>
    </Box>
  );

  const renderAlignmentStatus = (isAligned: boolean): JSX.Element => (
    <Box
      sx={{
        position: 'absolute',
        top: isMobile ? '18%' : '1rem',
        left: isMobile ? 'auto' : '1rem',
        right: isMobile ? '-7%' : 'auto',
        width: '100px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transform: isMobile ? 'translateY(-50%) rotate(90deg)' : 'none',
        transformOrigin: isMobile ? 'center center' : 'none',
        backgroundColor: isAligned
          ? 'rgba(76, 175, 80, 0.9)'
          : 'rgba(255, 152, 0, 0.9)',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '20px',
        fontSize: '0.875rem',
        fontWeight: 600,
        zIndex: 2,
        whiteSpace: 'nowrap',
      }}
    >
      {isAligned ? '✓ Aligned' : '⚠ Align Check'}
    </Box>
  );

  const renderGuidingFrame = (
    isAligned: boolean,
    countdown: number | null,
  ): JSX.Element => {
    const dimensions = getDimensions();
    const {
      containerWidth,
      containerHeight,
      guidingFrameWidth,
      guidingFrameHeight,
    } = dimensions;

    // Center the guiding frame
    const left = (containerWidth - guidingFrameWidth) / 2;
    const top = (containerHeight - guidingFrameHeight) / 2;

    // Debug aspect ratio calculations
    const actualAspectRatio = guidingFrameHeight / guidingFrameWidth;
    console.log('🔧 Guiding Frame Debug:', {
      isMobile,
      targetAspectRatio: aspectRatio,
      actualAspectRatio,
      dimensions: {
        containerWidth,
        containerHeight,
        guidingFrameWidth,
        guidingFrameHeight,
        left,
        top,
      },
      aspectRatioMatch: Math.abs(actualAspectRatio - aspectRatio) < 0.01,
    });

    return (
      <Box
        sx={{
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${guidingFrameWidth}px`,
          height: `${guidingFrameHeight}px`,
          border: `2px dashed ${isAligned ? '#4caf50' : '#ffffff'}`,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          zIndex: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: isAligned ? '#4caf50' : '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            textAlign: 'center',
            cursor: 'pointer',
            fontSize: isMobile ? '0.875rem' : '0.75rem',
            maxWidth: isMobile ? '100%' : '90%',
            width: '100%',
            wordWrap: 'break-word',
            transform: isMobile ? 'rotate(90deg)' : 'none',
            transformOrigin: 'center center',
            whiteSpace: 'normal',
          }}
        >
          {countdown
            ? `Auto-capturing in ${countdown}...`
            : isAligned
              ? 'Document aligned! ✓'
              : 'Position document within frame'}
        </Typography>
      </Box>
    );
  };

  const renderCameraView = (
    webcamRef: React.RefObject<Webcam>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
    isAligned: boolean,
    countdown: number | null,
  ): JSX.Element => {
    // Show loading if webcam props not ready
    if (!webcamProps) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            color: 'white',
          }}
        >
          <Typography variant="h6">Initializing camera...</Typography>
        </Box>
      );
    }

    return (
      <>
        <Webcam ref={webcamRef} {...webcamProps} />
        {renderGuidingFrame(isAligned, countdown)}
        {renderAlignmentStatus(isAligned)}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </>
    );
  };

  const renderCapturedView = (capturedImage: string): JSX.Element => (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <img
        src={capturedImage}
        alt="Captured document"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: 'transparent',
          transform: 'rotate(90deg)',
        }}
      />
    </Box>
  );

  const renderActionButtons = (
    capturedImage: string | null,
    isOpenCVReady: boolean,
    onCapture: () => void,
    onRetake: () => void,
    onUseImage: () => void,
  ): JSX.Element => {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: isMobile ? '2rem' : '1rem',
          marginTop: isMobile ? '1rem' : '2rem',
          padding: '0 2rem',
          position: isMobile ? 'absolute' : 'relative',
          bottom: isMobile ? '2rem' : 'auto',
          left: isMobile ? '50%' : 'auto',
          transform: isMobile ? 'translateX(-50%)' : 'none',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {!capturedImage ? (
          isMobile ? (
            <IconButton
              onClick={onCapture}
              disabled={!isOpenCVReady}
              sx={{
                backgroundColor: '#254D73',
                color: 'white',
                width: 60,
                height: 60,
                border: '3px solid white',
                '&:hover': {
                  backgroundColor: '#1e3a56',
                },
              }}
            >
              <ICONS.BiCircle size={32} />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              onClick={onCapture}
              disabled={!isOpenCVReady}
              sx={{
                backgroundColor: '#254D73',
                color: 'white',
                fontSize: '1.2rem',
                fontWeight: 700,
                padding: '0.8rem 2rem',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#254D73',
                },
              }}
            >
              {!isOpenCVReady ? 'Loading...' : 'Capture'}
            </Button>
          )
        ) : (
          <>
            {isMobile ? (
              <IconButton
                onClick={onRetake}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#666',
                  border: '2px solid white',
                  width: 60,
                  height: 60,
                  transform: 'rotate(90deg)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    color: '#333',
                  },
                }}
              >
                <ICONS.RefreshIcon size={28} />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                onClick={onRetake}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  padding: '0.8rem 2rem',
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: '#f0f0f0',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Retake
              </Button>
            )}

            {isMobile ? (
              <IconButton
                onClick={onUseImage}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  width: 60,
                  height: 60,
                  transform: 'rotate(90deg)',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                <ICONS.CheckmarkIcon size={28} />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                onClick={onUseImage}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  padding: '0.8rem 2rem',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                Use Image
              </Button>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderCameraContainer = (
    capturedImage: string | null,
    isAligned: boolean,
    webcamRef: React.RefObject<Webcam>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
    countdown: number | null,
  ): JSX.Element => {
    const dimensions = getDimensions();
    const { containerWidth, containerHeight } = dimensions;

    const containerStyle = isMobile
      ? {
          position: 'relative' as const,
          width: '100%',
          height: '100vh',
          borderRadius: '4px',
          overflow: 'hidden',
        }
      : {
          position: 'relative' as const,
          width: `${containerWidth}px`,
          height: `${containerHeight}px`,
          borderRadius: '4px',
          overflow: 'hidden',
        };

    return (
      <Box
        sx={{
          ...containerStyle,
          border: capturedImage
            ? 'none'
            : `3px solid ${isAligned ? '#4caf50' : '#254D73'}`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        {!capturedImage
          ? renderCameraView(webcamRef, canvasRef, isAligned, countdown)
          : renderCapturedView(capturedImage)}
      </Box>
    );
  };

  // Main render
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: isMobile ? 'flex-start' : 'center',
        overflow: 'hidden',
        // Prevent scrolling and zooming on mobile
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {renderHeader(title, onClose)}

      {renderCameraContainer(
        capturedImage,
        isAligned,
        webcamRef,
        canvasRef,
        countdown,
      )}

      {renderActionButtons(
        capturedImage,
        isOpenCVReady,
        handleCapture,
        handleRetake,
        handleUseImage,
      )}
    </Box>
  );
};

export default CheckCameraCapture;
